import { useState } from 'react';
import clsx from 'clsx';
import {
  bulkUpdateOrder,
  OrderConflictError,
  type OrderDetail,
  type OrderItem,
} from '../../lib/orders';
import { formatCurrency } from '../../lib/format';
import { ITEM_STATUS_COLORS } from '../../lib/order-constants';
import { useOrderEditState } from '../../hooks/useOrderEditState';
import { PRIMARY_SKU_WEDERVERKOPER, isPackageSku, isPerStukPrimary } from '../../lib/order-edit-rules';
import { useWasserijItemDrawerStore } from '../../stores/wasserij-item-drawer-store';
import { ItemEditRow } from './ItemEditRow';
import { AddItemForm } from './AddItemForm';
import { DeleteItemDialog } from './DeleteItemDialog';
import { PhotosLink } from './OrderDetailView';
import { ReparatieIntentDialog } from './ReparatieIntentDialog';
import { ItemKortingControl } from './ItemKortingControl';
import { KlantEigenItemDialog } from './KlantEigenItemDialog';

interface EditableOrderItemsProps {
  order: OrderDetail;
  /**
   * Roep aan na een succesvolle save. `nieuweLastModifiedDate` komt uit de
   * Apex-respons; de parent moet de lokale order-state synchroon patchen
   * vóór een eventuele async refetch start, anders gebruikt een snelle
   * tweede save de stale LMD en krijg je 409 CONFLICT.
   */
  onSaved: (nieuweLastModifiedDate: string | null) => void;
  /**
   * Optioneel — als meegegeven verschijnt per primary item een
   * "+ Behandeling toevoegen" knop in de read-only view. De parent beheert
   * de dialog-state.
   */
  onAddBehandeling?: (parentItemId: string, parentName: string) => void;
  /**
   * Optioneel — wanneer meegegeven verschijnt in de read-only view een hover-
   * delete-knop per item-groep. De callback wordt aangeroepen na een geslaagde
   * delete; parent moet hetzelfde refresh-pad volgen als bij `onSaved`.
   */
  onItemDeleted?: (nieuweLastModifiedDate: string | null) => void;
  /**
   * Optioneel — refresh-hook na het schrijven van een klantverzoek-intent
   * (set/clear). Als parent dit niet meegeeft is het potlood-icoon verborgen.
   */
  onIntentChanged?: () => void;
  /**
   * Optioneel — opent de edit-dialog voor het meegegeven item. Gebrukt door
   * de parent om EditWasserijItemDialog state te beheren.
   */
  onEditItem?: (item: OrderItem) => void;
  /**
   * Optioneel — verwijdert één behandeling-row (paid extra) zonder de hele
   * item-cascade te raken. Parent moet zelf refreshen + errors tonen.
   */
  onRemovePaidExtra?: (childItemId: string) => void;
  /**
   * Optioneel — als meegegeven verschijnt per regel (kleed + behandelingen) een
   * snelle korting-control (gratis / %). De callback is de refresh-hook na een
   * geslaagde korting-mutatie; parent volgt hetzelfde pad als `onSaved`.
   */
  onKortingSaved?: (nieuweLastModifiedDate: string | null) => void;
  /**
   * Optioneel — als meegegeven verschijnt onder de items een
   * "+ Toeslag / kosten toevoegen" knop (Bijrijdertoeslag, Transportkosten, …).
   * De parent beheert de dialog-state.
   */
  onAddFee?: () => void;
  /**
   * Optioneel — als meegegeven verschijnt in de read-only view een snelle
   * "+ Vloerkleed toevoegen" knop die één kleed (incl. pakketkeuze) toevoegt
   * via een modal. De parent beheert de dialog-state. Meerdere kleden tegelijk
   * bewerken/toevoegen blijft via "Bewerken".
   */
  onAddKleed?: () => void;
}

export function EditableOrderItems({
  order,
  onSaved,
  onAddBehandeling,
  onItemDeleted,
  onIntentChanged,
  onEditItem,
  onRemovePaidExtra,
  onKortingSaved,
  onAddFee,
  onAddKleed,
}: EditableOrderItemsProps) {
  const {
    state,
    changes,
    enterEdit,
    exitEdit,
    updateDraft,
    removeItem,
    undoRemove,
    addItem,
    updateAddition,
    removeAddition,
    setSaving,
    setError,
  } = useOrderEditState(order);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showKlantEigen, setShowKlantEigen] = useState(false);
  const [deleteKlantEigenId, setDeleteKlantEigenId] = useState<string | null>(null);

  const isEditing = state.mode === 'edit';
  const preview = state.preview;
  const currentTotal = isEditing && preview ? preview.newTotalInclBtw : order.totals.totalInclBtw;
  const totalDiff = isEditing && preview ? preview.diffInclBtw : 0;
  const changeCount = changes.length;

  const handleSave = async () => {
    if (changes.length === 0) {
      exitEdit();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Geen expectedLastModifiedDate meegeven: SF async-automations (future
      // methods, queueables, post-DML flows) wijzigen de WO ná onze commit,
      // waardoor strikte LMD-equality voortdurend 409s gaf op opeenvolgende
      // saves. Audit-log dekt traceability; bij echt-gelijktijdige edits
      // bouwen we later een tijdlijn-conflict-detectie.
      const result = await bulkUpdateOrder(order.id, changes);
      exitEdit();
      onSaved(result.nieuweLastModifiedDate);
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError('Deze order is ondertussen gewijzigd door iemand anders. Ververs de pagina om de laatste versie te zien.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Onbekende fout bij opslaan');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">
          Items ({order.items.length})
        </h2>
        {!isEditing && (
          <button
            type="button"
            onClick={enterEdit}
            className="text-xs font-medium text-indigo-600 hover:underline"
          >
            ✎ Bewerken
          </button>
        )}
      </div>

      {isEditing && (
        <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3">
          <span className="text-xs text-amber-800">
            ⚠ Je bewerkt deze order. Klik Opslaan om de wijzigingen definitief te maken.
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exitEdit}
              className="text-xs text-slate-600 hover:text-slate-900"
              disabled={state.isSaving}
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={state.isSaving || changeCount === 0}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded',
                state.isSaving || changeCount === 0
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              )}
            >
              {state.isSaving ? 'Opslaan…' : `Opslaan (${changeCount} wijz.)`}
            </button>
          </div>
        </div>
      )}

      {state.error && (
        <div className="px-6 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700">
          {state.error}
        </div>
      )}

      {!isEditing ? (
        <div className="px-4 py-3 space-y-2">
          {order.items.length === 0 ? (
            <p className="py-6 text-sm text-slate-400 text-center">Geen items</p>
          ) : (
            order.items.map((group, idx) => (
              <ReadOnlyItemGroup
                key={group.primary.id}
                group={group}
                index={idx + 1}
                orderId={order.id}
                onAddBehandeling={onAddBehandeling}
                onDeleted={onItemDeleted}
                onIntentChanged={onIntentChanged}
                onEditItem={onEditItem}
                onRemovePaidExtra={onRemovePaidExtra}
                onKortingSaved={onKortingSaved}
              />
            ))
          )}
          {order.klantEigenItems && order.klantEigenItems.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                Losse items (klant-eigen, niet gefactureerd)
              </p>
              <div className="space-y-1">
                {order.klantEigenItems.map((it) => {
                  const dims = it.lengthCm && it.widthCm ? `${it.lengthCm}×${it.widthCm} cm` : null;
                  return (
                    <div key={it.id} className="flex items-center gap-2 text-sm group">
                      <span className="font-mono text-[11px] text-slate-400">{it.itemNumber}</span>
                      <span className="font-medium text-slate-700">{it.name}</span>
                      {dims && <span className="text-xs text-slate-400">{dims}</span>}
                      <span className="text-[11px] text-slate-400">· €0 · niet op factuur</span>
                      {onItemDeleted && (
                        <button
                          type="button"
                          onClick={() => setDeleteKlantEigenId(it.id)}
                          aria-label="Los item verwijderen"
                          className="ml-auto p-0.5 rounded text-slate-300 hover:text-red-600 hover:bg-red-50"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(onAddKleed || onAddFee || onKortingSaved) && (
            <div className="pt-1 flex items-center gap-4">
              {onAddKleed && (
                <button
                  type="button"
                  onClick={onAddKleed}
                  className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
                >
                  + Item toevoegen
                </button>
              )}
              {onAddFee && (
                <button
                  type="button"
                  onClick={onAddFee}
                  className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
                >
                  + Toeslag / kosten toevoegen
                </button>
              )}
              {onKortingSaved && (
                <button
                  type="button"
                  onClick={() => setShowKlantEigen(true)}
                  className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
                  title="Los klant-eigen item (ondertapijt, spanbanden, …) — €0, niet op factuur, gaat mee terug"
                >
                  + Los item taggen
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          {order.items.map((group) => {
            const draft = state.drafts.get(group.primary.id);
            if (!draft) return null;
            const isRemoved = state.removed.has(group.primary.id);
            const newPrice = preview?.perItem.find((p) => p.itemId === group.primary.id)?.newPriceInclBtw;
            return (
              <ItemEditRow
                key={group.primary.id}
                label={group.primary.name}
                draft={draft}
                oldPriceInclBtw={group.primary.priceInclBtw}
                newPriceInclBtw={newPrice ?? group.primary.priceInclBtw}
                isRemoved={isRemoved}
                onChange={(patch) => updateDraft(group.primary.id, patch)}
                onRemove={() => removeItem(group.primary.id)}
                onUndoRemove={() => undoRemove(group.primary.id)}
              />
            );
          })}

          {state.additions.map((add) => (
            <ItemEditRow
              key={add.tempId}
              label={
                add.primarySku === PRIMARY_SKU_WEDERVERKOPER
                  ? 'Nieuw wederverkoper-kleed'
                  : 'Nieuw vloerkleed'
              }
              draft={add}
              isAddition
              onChange={(patch) => updateAddition(add.tempId, patch)}
              onRemove={() => removeAddition(add.tempId)}
            />
          ))}

          <div className="px-6 py-3 border-t border-slate-100">
            {showAddForm ? (
              <AddItemForm
                onAdd={(draft) => {
                  addItem(draft);
                  setShowAddForm(false);
                }}
                onCancel={() => setShowAddForm(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="text-sm text-emerald-700 hover:text-emerald-900 font-medium"
              >
                + Kleed toevoegen
              </button>
            )}
          </div>
        </>
      )}

      <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 rounded-b-xl">
        <div className="flex justify-between text-xs text-slate-500">
          <span>Totaal excl. BTW (geschat)</span>
          <span>{formatCurrency(currentTotal / 1.21)}</span>
        </div>
        <div className="flex justify-between text-sm font-medium text-slate-900 mt-1">
          <span>Totaal incl. BTW</span>
          <span className="flex items-center gap-2">
            {isEditing && totalDiff !== 0 && (
              <span className={clsx('text-xs', totalDiff > 0 ? 'text-emerald-600' : 'text-red-600')}>
                {totalDiff > 0 ? '+' : ''}
                {formatCurrency(totalDiff)}
              </span>
            )}
            {isEditing && state.previewLoading && (
              <span className="text-xs text-slate-400">…</span>
            )}
            {formatCurrency(currentTotal)}
          </span>
        </div>
        {isEditing && preview?.bundelkorting?.applicable && (
          <div className="flex justify-between text-xs text-emerald-700 mt-1">
            <span>Bundelkorting ({Math.round(preview.bundelkorting.percentage * 100)}%)</span>
            <span>−{formatCurrency(preview.bundelkorting.bedrag)}</span>
          </div>
        )}
        {isEditing && !preview?.bundelkorting && (preview?.kortingTotaalInclBtw ?? 0) > 0 && (
          <div className="flex justify-between text-xs text-emerald-700 mt-1">
            <span>Korting totaal</span>
            <span>−{formatCurrency(preview!.kortingTotaalInclBtw!)}</span>
          </div>
        )}
      </div>

      <KlantEigenItemDialog
        isOpen={showKlantEigen}
        workOrderId={order.id}
        onClose={() => setShowKlantEigen(false)}
        onAdded={() => onKortingSaved?.(null)}
      />

      {onItemDeleted && (
        <DeleteItemDialog
          isOpen={deleteKlantEigenId !== null}
          wiId={deleteKlantEigenId ?? ''}
          orderId={order.id}
          onClose={() => setDeleteKlantEigenId(null)}
          onDeleted={(lmd) => { setDeleteKlantEigenId(null); onItemDeleted(lmd); }}
        />
      )}
    </div>
  );
}

function ReadOnlyItemGroup({
  group,
  index,
  orderId,
  onAddBehandeling,
  onDeleted,
  onIntentChanged,
  onEditItem,
  onRemovePaidExtra,
  onKortingSaved,
}: {
  group: OrderDetail['items'][number];
  index: number;
  orderId: string;
  onAddBehandeling?: (parentItemId: string, parentName: string) => void;
  onDeleted?: (nieuweLastModifiedDate: string | null) => void;
  onIntentChanged?: () => void;
  onEditItem?: (item: OrderItem) => void;
  onRemovePaidExtra?: (childItemId: string) => void;
  onKortingSaved?: (nieuweLastModifiedDate: string | null) => void;
}) {
  const openDrawer = useWasserijItemDrawerStore((s) => s.open);
  const [showDelete, setShowDelete] = useState(false);
  const [showIntent, setShowIntent] = useState(false);
  const item = group.primary;
  // Primaire kleden hebben dimensies; alleen daar tonen we de intent-knop.
  const isPrimaryKleed =
    item.areaM2 != null || item.lengthCm != null || item.diameterCm != null;
  const intentStatus = item.klantverzoekStatus ?? null;
  const subtotal =
    item.priceInclBtw + group.extras.reduce((s, e) => s + e.priceInclBtw, 0);
  // Een regel-eigen handmatige korting (Andere_prijs + Korting>0) maakt een
  // behandeling gratis of afgeprijsd. Die tonen we als eigen regel met badge,
  // niet als stille "inbegrepen" naam — anders verdwijnt een gratis-gegeven
  // behandeling visueel tussen de pakket-inclusies.
  const manualKorting = (it: OrderItem): number | null =>
    it.anderePrijs && (it.korting ?? 0) > 0 ? (it.korting as number) : null;
  const isShownExtra = (e: OrderItem) => e.priceInclBtw !== 0 || manualKorting(e) != null;
  const includedExtras = group.extras.filter((e) => !isShownExtra(e));
  const paidExtras = group.extras.filter(isShownExtra);
  const primaryKorting = manualKorting(item);
  const dimensions = item.isRound && item.diameterCm
    ? `Ø ${item.diameterCm} cm`
    : item.lengthCm && item.widthCm
      ? `${item.lengthCm}×${item.widthCm} cm`
      : '';
  // Aantal__c valt bij kleden samen met Aantal_m__c (oppervlakte).
  // Alleen `×N` tonen wanneer quantity écht een stukstal is.
  const showQuantity =
    item.quantity > 1 &&
    (item.areaM2 == null || Math.abs(item.quantity - item.areaM2) > 0.01);

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-start">
        <button
          type="button"
          onClick={() => openDrawer(item.id)}
          className="flex-1 min-w-0 text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors"
        >
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-medium flex items-center justify-center tabular-nums mt-0.5">
          {index}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-900 truncate">
              {item.name || 'Item'}
            </span>
            {item.itemNumber && (
              <span className="text-[11px] text-slate-400 font-mono">{item.itemNumber}</span>
            )}
            {isPrimaryKleed && onIntentChanged && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowIntent(true);
                }}
                title={
                  item.klantverzoekReparatie
                    ? `Klantverzoek: ${item.klantverzoekReparatie}`
                    : 'Klantverzoek reparatie'
                }
                className={clsx(
                  'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition-colors',
                  intentStatus === 'Open' &&
                    'bg-amber-100 text-amber-800 hover:bg-amber-200',
                  intentStatus === 'Bevestigd' &&
                    'bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
                  intentStatus === 'Niet_Nodig' &&
                    'bg-slate-100 text-slate-500 hover:bg-slate-200',
                  !intentStatus &&
                    'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                )}
              >
                <span aria-hidden>🔧</span>
                {intentStatus === 'Open' && <span>Verzoek</span>}
                {intentStatus === 'Bevestigd' && <span>Bevestigd</span>}
                {intentStatus === 'Niet_Nodig' && <span>Niet nodig</span>}
              </button>
            )}
            {item.photosUrl && <PhotosLink url={item.photosUrl} />}
            {item.status && (
              <span
                className={clsx(
                  'text-[10px] px-1.5 py-0.5 rounded font-medium',
                  ITEM_STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-600'
                )}
              >
                {item.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-0.5 flex-wrap">
            {dimensions && <span>{dimensions}</span>}
            {item.areaM2 != null && <span>{item.areaM2.toFixed(2)} m²</span>}
            {showQuantity && <span>×{item.quantity}</span>}
            {includedExtras.length > 0 && (
              <span className="text-slate-600">
                {includedExtras.map((e) => e.name).join(' · ')}
              </span>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-slate-600 mt-1.5 whitespace-pre-line">
              {item.description}
            </p>
          )}
        </div>
        </button>
        <div className="flex items-start gap-1 px-3 py-3 flex-shrink-0">
          <span className="text-sm font-semibold text-slate-900 tabular-nums">
            {formatCurrency(subtotal)}
          </span>
          {(onEditItem || onDeleted) && (
            <div className="flex items-center gap-0.5">
              {/* Per-stuk items (Kussen) hebben geen maten/pakket; deze dialog is
                  daarvoor. Aantal wijzigen gaat via de item-drawer (KleedCard). */}
              {onEditItem && !isPerStukPrimary(item.productCode) && (
                <button
                  type="button"
                  onClick={() => onEditItem(item)}
                  aria-label="Item bewerken"
                  title="Maten, pakket of bijzonderheden bewerken"
                  className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                  </svg>
                </button>
              )}
              {onDeleted && (
                <button
                  type="button"
                  onClick={() => setShowDelete(true)}
                  aria-label="Verwijder item"
                  title="Kleed verwijderen"
                  className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {paidExtras.length > 0 && (
        <div className="border-t border-dashed border-slate-200 bg-slate-50/60 px-4 py-1.5">
          {paidExtras.map((extra) => {
            // Een pakket-regel (Premium Plus, Huisdier Compleet, …) is geen losse
            // behandeling: Apex weigert een directe extra-remove ("gaat via
            // setPackage"). Daarom bieden we hier "Wijzigen" aan i.p.v.
            // verwijderen — dat opent de pakketkeuze op het primaire kleed.
            const isPackage = isPackageSku(extra.productCode);
            return (
            <div
              key={extra.id}
              className="w-full flex items-center justify-between gap-3 py-1 text-xs text-slate-600"
            >
              <button
                type="button"
                onClick={() => openDrawer(extra.id)}
                className="flex items-center gap-1.5 min-w-0 flex-1 text-left hover:text-slate-900 transition-colors"
              >
                <span className="text-slate-300">↳</span>
                <span className="truncate">{extra.name || 'Extra'}</span>
              </button>
              <div className="flex items-center gap-2 flex-shrink-0">
                {onKortingSaved && (
                  <ItemKortingControl
                    itemId={extra.id}
                    currentPct={manualKorting(extra)}
                    onSaved={onKortingSaved}
                  />
                )}
                {extra.priceInclBtw === 0 ? (
                  <span className="font-medium text-emerald-700">Gratis</span>
                ) : (
                  <span className="tabular-nums">+ {formatCurrency(extra.priceInclBtw)}</span>
                )}
                {isPackage ? (
                  onEditItem && (
                    <button
                      type="button"
                      onClick={() => onEditItem(item)}
                      aria-label="Pakket wijzigen"
                      title="Ander pakket kiezen"
                      className="p-0.5 rounded text-slate-300 hover:text-indigo-600 hover:bg-indigo-50"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                      </svg>
                    </button>
                  )
                ) : (
                  onRemovePaidExtra && (
                    <button
                      type="button"
                      onClick={() => onRemovePaidExtra(extra.id)}
                      aria-label="Behandeling verwijderen"
                      className="p-0.5 rounded text-slate-300 hover:text-red-600 hover:bg-red-50"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  )
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {(onAddBehandeling || onKortingSaved) && (
        <div className="border-t border-slate-100 bg-white px-4 py-1.5 flex items-center justify-between gap-3">
          {onAddBehandeling ? (
            <button
              type="button"
              onClick={() => onAddBehandeling(item.id, item.name || 'Item')}
              className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
            >
              + Behandeling toevoegen
            </button>
          ) : (
            <span />
          )}
          {onKortingSaved && (
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              {primaryKorting != null && primaryKorting > 0 && 'Korting kleed:'}
              <ItemKortingControl
                itemId={item.id}
                currentPct={primaryKorting}
                label="+ Korting op kleed"
                onSaved={onKortingSaved}
              />
            </span>
          )}
        </div>
      )}

      {onDeleted && (
        <DeleteItemDialog
          isOpen={showDelete}
          wiId={item.id}
          orderId={orderId}
          onClose={() => setShowDelete(false)}
          onDeleted={(lmd) => onDeleted(lmd)}
        />
      )}

      {showIntent && onIntentChanged && (
        <ReparatieIntentDialog
          wiId={item.id}
          wiName={item.name || 'Item'}
          initialKlantverzoek={item.klantverzoekReparatie ?? null}
          initialStatus={item.klantverzoekStatus ?? null}
          initialNotitie={item.klantverzoekNotitie ?? null}
          onClose={() => setShowIntent(false)}
          onSaved={onIntentChanged}
        />
      )}
    </div>
  );
}
